import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000

// Namespaced per user: the cache is read synchronously on first paint, so a
// shared browser used to render the previous account's timezone labels and
// view preference until the fetch landed.
function cacheKey() {
  return `cal_settings:${window.CURRENT_USER?.id ?? 'anon'}`
}

function readCache() {
  try { return JSON.parse(localStorage.getItem(cacheKey()) || 'null') } catch { return null }
}

export const useCalendarSettingsStore = defineStore('calendarSettings', {
  state: () => {
    const c = readCache() ?? {}
    return {
      // All fields returned by CalendarSettingBlueprint
      id: null,
      locale: null,
      country_iso_code: null,
      display_secondary_time_zone: c.display_secondary_time_zone ?? false,
      secondary_timezone:          c.secondary_timezone          ?? null,
      default_view:                c.default_view                ?? null,
      reduce_brightness_of_past_bookings: c.reduce_brightness_of_past_bookings ?? false,
      show_declined_bookings:      c.show_declined_bookings      ?? true,  // matches the DB default
      collapse_duplicate_events:   c.collapse_duplicate_events   ?? false,
      show_now_indicator:          c.show_now_indicator          ?? true,
      narrow_weekend:              c.narrow_weekend              ?? false,
      show_only_work_week:         c.show_only_work_week         ?? false,
      date_primary_timezone:       c.date_primary_timezone       ?? null,
      date_secondary_timezone:     c.date_secondary_timezone     ?? null,
      label_for_secondary_timezone:           c.label_for_secondary_timezone           ?? null,
      formatted_label_for_primary_timezone:   c.formatted_label_for_primary_timezone   ?? null,
      formatted_label_for_secondary_timezone: c.formatted_label_for_secondary_timezone ?? null,
      show_past_bookings:          c.show_past_bookings          ?? true,

      // Cache metadata — always null so we re-fetch on every page load
      fetchedAt: null,
      loading: false,
      error: null,
    }
  },

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid) return
      await this.fetchSettings()
    },

    async fetchSettings() {
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/calendar/settings', {}, false)
        if (!res.js_error && res.response?.success) {
          this.updateSettings(res.response.data)
        } else {
          this.error = res.response?.message || 'Failed to load calendar settings.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    async saveSettings(patch) {
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest(
          'PUT',
          '/api/v1/calendar/settings',
          { calendar_setting: patch },
          false
        )
        if (!res.js_error && res.response?.success) {
          this.updateSettings(res.response.data)
        } else {
          this.error = res.response?.message || 'Failed to save calendar settings.'
        }
      } catch {
        this.error = 'Something went wrong.'
      }
    },

    updateSettings(patch) {
      const settingsFields = [
        'id', 'locale', 'country_iso_code',
        'display_secondary_time_zone', 'secondary_timezone',
        'default_view', 'reduce_brightness_of_past_bookings', 'show_declined_bookings',
        'collapse_duplicate_events', 'show_now_indicator', 'narrow_weekend',
        'show_only_work_week', 'date_primary_timezone',
        'date_secondary_timezone',
        'label_for_secondary_timezone', 'formatted_label_for_primary_timezone',
        'formatted_label_for_secondary_timezone', 'show_past_bookings',
      ]
      settingsFields.forEach(key => {
        if (key in patch) this[key] = patch[key]
      })
      this.fetchedAt = Date.now()

      // Persist to localStorage so next page load renders instantly with correct settings
      try {
        const toCache = {}
        settingsFields.forEach(k => { toCache[k] = this[k] })
        localStorage.setItem(cacheKey(), JSON.stringify(toCache))
      } catch {}
    },

    invalidate() {
      this.fetchedAt = null
    },
  },
})
